import React, { useContext } from "react";
import { useState } from "react";
import { useEffect } from "react";
import { useMemo } from "react";

import { Typography } from "@mui/material";
import { useMediaQuery } from "@mui/material";

import ScenarioSuggestions from "./ScenarioSuggestions";

import PageOption from "../../PageTypes/PageOption";

import Skeleton from "@mui/material/Skeleton";

import ScenarioSimpleDisplay from "./ScenarioSimpleDisplay";
import ScenarioDisplay from "./ScenarioDisplay";
import ScenarioDialog from "./ScenarioConfigurationComponents/ScenarioDialog";

import PersonalizationContext from "../../PersonalizationComponents/PersonalizationContext";

import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";

import FormRowState from "../UtilityComponents/FormUtilityComponents/FormRowState";
import SelectedScenario from "./SelectedScenario";
import PeriodType from "../UtilityComponents/GraphOptionUtilityComponents/PeriodType";
import { defaultNoScenarioDisplayProps } from "./DefaultNoScenarioDisplayProps";

import ScenarioCard from "./ScenarioCardComponents/ScenarioCard";
import AddScenarioCard from "./ScenarioCardComponents/AddScenarioCard";
import { useTheme } from "@mui/material/styles";

import CallToActionBox from "../UtilityComponents/PaperUtilityComponents/CallToActionBox";
import Collapse from "@mui/material/Collapse";

import ErrorPage from "../../ErrorPageComponents/ErrorPage";
import FormConfig from "../UtilityComponents/FormUtilityComponents/FormConfig";

import ShadowPaper from "../UtilityComponents/PaperUtilityComponents/ShadowPaper";

import findNextUnusedName from "../UtilityComponents/FormUtilityComponents/findNextUnusedName";

import snackbarScenarioChange from "./snackbarScenarioChange";
import PostSelectedScenarioList from "./PostSelectedScenarioList";

import { useSearchParams } from "react-router-dom";
import { isMobile } from "react-device-detect";

const paperPadding = 2;

function getCurrentlyDisplayedScenarioDisplay(scenarioList: SelectedScenario[]) {
  if (scenarioList === undefined) {
    return undefined;
  }
  const currentlyDisplayedScenarioList = scenarioList.filter((scenario) => scenario.currentlyDisplayed);
  return currentlyDisplayedScenarioList.length ? currentlyDisplayedScenarioList[0] : undefined;
}

function getPrimaryScenarioWithIndex(scenarioList: SelectedScenario[]) {
  if (scenarioList === undefined) {
    return {
      lockedScenario: undefined,
      lockedIndex: -1,
    };
  }
  const scenarioListWithIndexes = scenarioList.map((scenario, index) => {
    return { lockedScenario: scenario, lockedIndex: index };
  });

  const lockedInScenario = scenarioListWithIndexes.filter((scenario) => scenario.lockedScenario.primary);
  if (lockedInScenario.length) {
    return lockedInScenario[0];
  }
  return {
    lockedScenario: undefined,
    lockedIndex: -1,
  };
}

function getCurrentlyDisplayedScenarioDisplayProps(scenarioList: SelectedScenario[], nonePreSelected?: boolean) {
  if (scenarioList === undefined) {
    return { ...defaultNoScenarioDisplayProps, noneSelected: true };
  }
  const currentlyDisplayedScenarioList = scenarioList.filter((scenario) => scenario.currentlyDisplayed);
  return currentlyDisplayedScenarioList.length
    ? currentlyDisplayedScenarioList[0].scenarioDisplayProps
    : { ...defaultNoScenarioDisplayProps, noneSelected: nonePreSelected === undefined ? true : nonePreSelected };
}

function noneSelectedFrom(scenarioList: SelectedScenario[]) {
  if (scenarioList === undefined) {
    return true;
  }
  return scenarioList.filter((scenario) => scenario.currentlyDisplayed).length === 0;
}

function nonePrimaryFrom(scenarioList: SelectedScenario[]) {
  if (scenarioList === undefined) {
    return true;
  }
  return scenarioList.filter((scenario) => scenario.primary).length === 0;
}

function selectedIsPrimaryFrom(scenarioList: SelectedScenario[]) {
  if (scenarioList === undefined) {
    return false;
  }
  return scenarioList.filter((scenario) => scenario.currentlyDisplayed && scenario.primary).length === 1;
}

type ScenariosPanelProps = {
  addSnackbarMessage: (title: string, message: string) => void;
  firstChecksDone: boolean;
  scenario: string;
  setScenario: (newScenario: string) => void;
  selectedScenarioList: SelectedScenario[];
  setSelectedScenarioList: (newSelectedScenarioList: SelectedScenario[]) => void;
  scenarioRetrievalErrored: boolean;
  scenarioOptionsConfigs: { [key: string]: FormConfig };
  scenarioOptionsRowStates: { [key: string]: FormRowState[] }; // Contains information about current form values in dialog for each scenario
  setScenarioOptionsRowStates: (newScenarioOptionsRowStates: { [key: string]: FormRowState[] }) => void;
  primaryStrategy: string | undefined;
  strategyMetadata: { [key: string]: any };
  loading: boolean;
  setLoading: (newLoading: boolean) => void;
  dialogOpen: boolean;
  setDialogOpen: (newDialogOpen: boolean) => void;
  setCloseDialogOnGraphUpdate: (newCloseDialogOnGraphUpdate: boolean) => void;
  completeLoadingOnDialogClose: boolean;
  setCompleteLoadingOnDialogClose: (newCompleteLoadingOnDialogClose: boolean) => void;
  historicalPeriod: PeriodType;
  setHistoricalPeriod: (newHistoricalPeriod: PeriodType) => void;
  futurePeriod: PeriodType;
  setFuturePeriod: (newFuturePeriod: PeriodType) => void;
  lastDrawnTotalValue: number | undefined;
  setLastDrawnTotalValue: (value: number | undefined) => void;
  lastDrawnCoreStockValue: number | undefined;
  setLastDrawnCoreStockValue: (value: number | undefined) => void;
  lastDrawnOtherStockValue: number | undefined;
  setLastDrawnOtherStockValue: (value: number | undefined) => void;
  lastDrawnCashValue: number | undefined;
  setLastDrawnCashValue: (value: number | undefined) => void;
  noStockPlansAdded: boolean;
  noESPPPlansAdded: boolean;
  setPage: (newPage: PageOption) => void;
  immediateStockPlanAddPrompt: () => void;
  immediateESPPAddPrompt: () => void;
  handleOverviewUpdateNotification: () => void;
  withholdingOn: boolean;
  withholdingRate: number;
  firstWithholdingCheckDone: boolean;
};

function ScenariosPanel(props: ScenariosPanelProps) {
  const theme = useTheme();
  const fixedSizeForSideSquares = theme.spacing(50);
  // all the lifted state
  const scenario = props.scenario;
  const setScenario = props.setScenario;
  const selectedScenarioList = props.selectedScenarioList;
  const setSelectedScenarioList = props.setSelectedScenarioList;

  const [dialogIsNew, setDialogIsNew] = useState(true);
  const [scenarioEdited, setScenarioEdited] = useState(false); // TODO: still hits backend if params changed and then changed back
  const [scenarioListEditingIndex, setScenarioListEditingIndex] = useState(-1);

  const noPlansAdded = props.noStockPlansAdded && props.noESPPPlansAdded;

  // const [displayCardLoadingSymbol, setDisplayCardLoadingSymbol] = useState(false);
  const [scenarioToDisplayOrEditOrAdd, setScenarioToDisplayOrEditOrAdd] = useState<
    | {
        addScenario: string | undefined;
        editDisplayScenario: SelectedScenario | undefined;
        index: number;
        editDisplayAdd: "edit" | "display" | "add";
      }
    | undefined
  >(undefined);

  const disableAll = (scenarioList: SelectedScenario[]) =>
    scenarioList ? scenarioList.map((scenario) => ({ ...scenario, currentlyDisplayed: false })) : [];

  const demoteAll = (scenarioList: SelectedScenario[]) =>
    scenarioList ? scenarioList.map((scenario) => ({ ...scenario, primary: false })) : [];

  const disableAndDemoteAll = (scenarioList: SelectedScenario[]) =>
    scenarioList ? scenarioList.map((scenario) => ({ ...scenario, currentlyDisplayed: false, primary: false })) : [];

  function startEdit(scenario: SelectedScenario, index: number) {
    setSelectedScenarioList([
      ...disableAll(selectedScenarioList.slice(0, index)),
      { ...selectedScenarioList[index], currentlyDisplayed: true },
      ...disableAll(selectedScenarioList.slice(index + 1)),
    ]);
    setScenario(scenario.scenario);
    setDialogIsNew(false);
    setScenarioEdited(false);
    props.setDialogOpen(true);
    props.setScenarioOptionsRowStates({
      ...props.scenarioOptionsRowStates,
      [scenario.scenario]: scenario.params,
    });
    setScenarioListEditingIndex(index);
  }

  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamEdit = searchParams.get("edit")?.toLowerCase();

  if (searchParamEdit === "locked_forecast") {
    setSearchParams((oldParams) => {
      oldParams.delete("edit");
      return oldParams;
    });
    const { lockedScenario, lockedIndex } = getPrimaryScenarioWithIndex(props.selectedScenarioList);
    if (lockedIndex >= 0 && lockedScenario !== undefined) {
      props.loading
        ? setScenarioToDisplayOrEditOrAdd({
            addScenario: undefined,
            editDisplayScenario: lockedScenario,
            index: lockedIndex,
            editDisplayAdd: "edit",
          })
        : startEdit(lockedScenario, lockedIndex);
    }
  }

  useEffect(() => {
    setSearchParams((oldParams) => {
      oldParams.delete("edit");
      return oldParams;
    });
  }, [setSearchParams]);

  useEffect(() => {
    if (!props.loading && scenarioToDisplayOrEditOrAdd !== undefined) {
      if (
        scenarioToDisplayOrEditOrAdd.editDisplayAdd === "edit" &&
        scenarioToDisplayOrEditOrAdd.editDisplayScenario !== undefined
      ) {
        startEdit(scenarioToDisplayOrEditOrAdd.editDisplayScenario, scenarioToDisplayOrEditOrAdd.index);
      } else if (
        scenarioToDisplayOrEditOrAdd.editDisplayAdd === "display" &&
        scenarioToDisplayOrEditOrAdd.editDisplayScenario !== undefined
      ) {
        setScenario(scenarioToDisplayOrEditOrAdd.editDisplayScenario.scenario);
        setSelectedScenarioList([
          ...disableAll(selectedScenarioList.slice(0, scenarioToDisplayOrEditOrAdd.index)),
          { ...selectedScenarioList[scenarioToDisplayOrEditOrAdd.index], currentlyDisplayed: true },
          ...disableAll(selectedScenarioList.slice(scenarioToDisplayOrEditOrAdd.index + 1)),
        ]);
      } else if (
        scenarioToDisplayOrEditOrAdd.editDisplayAdd === "add" &&
        scenarioToDisplayOrEditOrAdd.addScenario !== undefined
      ) {
        handleScenarioAdd(scenarioToDisplayOrEditOrAdd.addScenario);
      }
      setScenarioToDisplayOrEditOrAdd(undefined);
    }
  }, [props.loading]);

  function handleScenarioAdd(newScenario?: string) {
    if (newScenario !== undefined) {
      setScenario(newScenario);
    }

    const scenarioToAdd = newScenario || scenario;
    const usedNames = selectedScenarioList.map(
      (ss) => ss.params[0].currentModeStates[ss.params[0].currentModeIndex] as string
    );

    const candidateName =
      (props.scenarioOptionsRowStates[scenarioToAdd] &&
        props.scenarioOptionsRowStates[scenarioToAdd][0] &&
        (props.scenarioOptionsRowStates[scenarioToAdd][0].currentModeStates[0] as string)) ||
      scenarioToAdd;

    const overrideNickname = usedNames.includes(candidateName)
      ? findNextUnusedName(scenarioToAdd, usedNames)
      : candidateName;

    if (props.scenarioOptionsRowStates[scenarioToAdd] !== undefined) {
      const obj = {
        ...props.scenarioOptionsRowStates,
        [scenarioToAdd]: props.scenarioOptionsRowStates[scenarioToAdd].map((rowState, index) =>
          index === 0
            ? { currentModeIndex: 0, currentModeStates: [overrideNickname], currentModeErrors: [null] }
            : { ...rowState }
        ),
      };
      props.setScenarioOptionsRowStates(obj);
    }

    setDialogIsNew(true);
    props.setDialogOpen(true);
  }

  const maxPortfolioValues =
    selectedScenarioList === undefined
      ? undefined
      : Math.max(...selectedScenarioList.map((s) => s.scenarioDisplayProps.maxPortfolioValue || 0));
  const maxStockPrice =
    selectedScenarioList === undefined
      ? undefined
      : Math.max(...selectedScenarioList.map((s) => s.scenarioDisplayProps.maxStockPrice || 0));

  const scenarioDisplayProps = {
    ...getCurrentlyDisplayedScenarioDisplayProps(
      selectedScenarioList,
      scenarioToDisplayOrEditOrAdd === undefined || scenarioToDisplayOrEditOrAdd.editDisplayAdd === "add"
    ),
    historicalPeriod: props.historicalPeriod,
    setHistoricalPeriod: props.setHistoricalPeriod,
    futurePeriod: props.futurePeriod,
    setFuturePeriod: props.setFuturePeriod,
    lastDrawnTotalValue: props.lastDrawnTotalValue,
    setLastDrawnTotalValue: props.setLastDrawnTotalValue,
    lastDrawnCoreStockValue: props.lastDrawnCoreStockValue,
    setLastDrawnCoreStockValue: props.setLastDrawnCoreStockValue,
    lastDrawnOtherStockValue: props.lastDrawnOtherStockValue,
    setLastDrawnOtherStockValue: props.setLastDrawnOtherStockValue,
    lastDrawnCashValue: props.lastDrawnCashValue,
    setLastDrawnCashValue: props.setLastDrawnCashValue,
    maxPortfolioValue: maxPortfolioValues,
    maxStockPrice: maxStockPrice,
    scenarioOptions: props.scenarioOptionsConfigs,
    spinAddScenario:
      props.loading &&
      scenarioToDisplayOrEditOrAdd !== undefined &&
      scenarioToDisplayOrEditOrAdd.editDisplayAdd === "add"
        ? scenarioToDisplayOrEditOrAdd.addScenario
        : undefined,
    handleAdd: (scenario: string | undefined) => {
      if (props.loading) {
        setScenarioToDisplayOrEditOrAdd({
          addScenario: scenario,
          editDisplayScenario: undefined,
          index: -1,
          editDisplayAdd: "add",
        });
      } else {
        handleScenarioAdd(scenario);
      }
    },
  };

  const { personalizationConfig, setPersonalizationConfig } = useContext(PersonalizationContext);
  const isSimple = "complexity" in personalizationConfig && personalizationConfig.complexity === "simple";

  const mainDisplayContents = isSimple ? (
    <ScenarioSimpleDisplay {...scenarioDisplayProps} />
  ) : (
    <ScenarioDisplay {...scenarioDisplayProps} />
  );

  const scenarioCardsStackContents =
    selectedScenarioList &&
    selectedScenarioList.map((scenario, index) => (
      <ScenarioCard
        key={index}
        supratitle={scenario.scenario}
        scenarioRetrievalErrored={props.scenarioRetrievalErrored}
        // Assumes that the nickname for the scenario is the 0th param in the form params
        title={
          (scenario !== undefined &&
            scenario.params &&
            scenario.params[0] &&
            (scenario.params[0].currentModeStates[scenario.params[0].currentModeIndex] as string)) ||
          scenario.scenario
        }
        scenarioDisplayProps={{ ...scenario.scenarioDisplayProps, futurePeriod: props.futurePeriod }}
        description={
          (props.scenarioOptionsConfigs[scenario.scenario] &&
            props.scenarioOptionsConfigs[scenario.scenario].description) ||
          ""
        }
        selected={scenario.currentlyDisplayed}
        primary={scenario.primary}
        onPrimaryClick={() => {
          if (props.loading) {
            return;
          } else {
            setScenario(scenario.scenario);
            const newSelectedScenarioList = [
              ...disableAndDemoteAll(selectedScenarioList.slice(0, index)),
              { ...scenario, currentlyDisplayed: true, primary: true },
              ...disableAndDemoteAll(selectedScenarioList.slice(index + 1)),
            ];
            setSelectedScenarioList(newSelectedScenarioList);
            snackbarScenarioChange(props.addSnackbarMessage, scenario);
            PostSelectedScenarioList(newSelectedScenarioList, props.handleOverviewUpdateNotification);
          }
        }}
        onCardClick={() => {
          if (props.loading) {
            setScenarioToDisplayOrEditOrAdd({
              addScenario: undefined,
              editDisplayScenario: scenario,
              index: index,
              editDisplayAdd: "display",
            });
          } else if (scenario.currentlyDisplayed) {
            startEdit(scenario, index);
          } else {
            setScenario(scenario.scenario);
            setSelectedScenarioList([
              ...disableAll(selectedScenarioList.slice(0, index)),
              { ...scenario, currentlyDisplayed: true },
              ...disableAll(selectedScenarioList.slice(index + 1)),
            ]);
          }
        }}
        onEditClick={() =>
          props.loading
            ? setScenarioToDisplayOrEditOrAdd({
                addScenario: undefined,
                editDisplayScenario: scenario,
                index: index,
                editDisplayAdd: "edit",
              })
            : startEdit(scenario, index)
        }
        loading={
          scenarioToDisplayOrEditOrAdd !== undefined &&
          scenarioToDisplayOrEditOrAdd.editDisplayAdd !== "add" &&
          scenarioToDisplayOrEditOrAdd.index === index
        }
      />
    ));

  const scenarioCardsStackAddCard = (
    <AddScenarioCard
      currentScenario={scenario}
      currentDescription={
        (props.scenarioOptionsConfigs[scenario] && props.scenarioOptionsConfigs[scenario].description) || ""
      }
      setScenario={setScenario}
      scenarioOptions={Object.keys(props.scenarioOptionsConfigs)}
      handleInfoClick={() => props.setSelectedScenarioList(disableAll(props.selectedScenarioList))}
      handleAdd={() => handleScenarioAdd(scenario)}
      loading={props.loading}
    />
  );

  function handleAddOrSaveGenerator(
    scenario: string,
    dialogIsNew: boolean,
    isPrimary: boolean,
    scenarioEdited: boolean
  ) {
    // does the special suggestions if scenario is 'SUGGESTED'
    function handleAddOrSave(state: FormRowState[]) {
      props.setLoading(true);
      var preScenarioList;
      var postScenarioList: SelectedScenario[];

      const needToUpdateFromBackend = dialogIsNew || scenarioEdited;

      if (dialogIsNew) {
        preScenarioList = disableAll(selectedScenarioList);
        postScenarioList = [];
      } else {
        preScenarioList = disableAll(selectedScenarioList.slice(0, scenarioListEditingIndex));
        postScenarioList = disableAll(selectedScenarioList.slice(scenarioListEditingIndex + 1));
      }

      var addedOrSavedScenario = [];

      if (scenario === "SUGGESTED") {
        const scenarioSuggestions = ScenarioSuggestions(selectedScenarioList);
        addedOrSavedScenario = scenarioSuggestions.suggestions;
        if (scenarioSuggestions.snackbarPrimaryName !== undefined) {
          snackbarScenarioChange(props.addSnackbarMessage, scenarioSuggestions.snackbarPrimaryName);
        }
      } else {
        addedOrSavedScenario = [
          {
            scenario: scenario,
            params: state,
            currentlyDisplayed: true,
            primary: !dialogIsNew && isPrimary,
            scenarioDisplayProps: needToUpdateFromBackend
              ? defaultNoScenarioDisplayProps
              : { ...selectedScenarioList[scenarioListEditingIndex].scenarioDisplayProps }, // copy over graph if no need to update
          },
        ];
      }

      const newSelectedScenarioList = [...preScenarioList, ...addedOrSavedScenario, ...postScenarioList];
      setSelectedScenarioList(newSelectedScenarioList); // TODO; change the action items from this?
      PostSelectedScenarioList(newSelectedScenarioList, props.handleOverviewUpdateNotification);

      if (needToUpdateFromBackend) {
        props.setCloseDialogOnGraphUpdate(true);
      } else {
        props.setDialogOpen(false);
      }
    }
    return handleAddOrSave;
  }

  const scenarioDialog =
    scenario === "" ? null : (
      <ScenarioDialog
        dialogTitle={(dialogIsNew ? "Configure " : "Edit ") + scenario}
        description={
          (props.scenarioOptionsConfigs[scenario] && props.scenarioOptionsConfigs[scenario].description) || ""
        }
        open={props.dialogOpen}
        loading={props.loading}
        config={props.scenarioOptionsConfigs[scenario]}
        rowStateList={props.scenarioOptionsRowStates[scenario]}
        disallowedNames={
          selectedScenarioList === undefined
            ? []
            : dialogIsNew
            ? selectedScenarioList.map((ss) => ss.params[0].currentModeStates[ss.params[0].currentModeIndex] as string)
            : selectedScenarioList
                .filter((ss, index) => index !== scenarioListEditingIndex)
                .map((ss) => ss.params[0].currentModeStates[ss.params[0].currentModeIndex] as string)
        }
        setRowStateList={(newRowStateList) => {
          setScenarioEdited(true);
          props.setScenarioOptionsRowStates({
            ...props.scenarioOptionsRowStates,
            [scenario]:
              typeof newRowStateList === "function"
                ? newRowStateList(props.scenarioOptionsRowStates[scenario])
                : newRowStateList,
          });
        }}
        handleCancel={() => {
          if (!props.loading) props.setDialogOpen(false);
        }}
        isNew={dialogIsNew}
        isPrimary={
          !dialogIsNew &&
          selectedScenarioList &&
          scenarioListEditingIndex >= 0 &&
          scenarioListEditingIndex < selectedScenarioList.length &&
          selectedScenarioList[scenarioListEditingIndex].primary
        }
        handleAddOrSave={handleAddOrSaveGenerator(
          scenario,
          dialogIsNew,
          !dialogIsNew &&
            scenarioListEditingIndex >= 0 &&
            scenarioListEditingIndex < selectedScenarioList.length &&
            selectedScenarioList[scenarioListEditingIndex].primary,
          !dialogIsNew && scenarioEdited
        )}
        handleDelete={() => {
          props.setLoading(true);
          const newSelectedScenarioList = [
            ...disableAll(selectedScenarioList.slice(0, scenarioListEditingIndex)),
            ...disableAll(selectedScenarioList.slice(scenarioListEditingIndex + 1)),
          ];
          setSelectedScenarioList(newSelectedScenarioList);
          PostSelectedScenarioList(newSelectedScenarioList, props.handleOverviewUpdateNotification);
          props.setCloseDialogOnGraphUpdate(true);
        }}
        completeLoadingOnDialogClose={props.completeLoadingOnDialogClose}
        setCompleteLoadingOnDialogClose={props.setCompleteLoadingOnDialogClose}
        setLoading={props.setLoading}
      />
    );
  const taxSavings = getCurrentlyDisplayedScenarioDisplayProps(selectedScenarioList).taxSavings;

  const noScenarios = useMemo(
    () => selectedScenarioList === undefined || selectedScenarioList.length === 0,
    [selectedScenarioList]
  );

  const noneSelected = useMemo(() => noneSelectedFrom(selectedScenarioList), [selectedScenarioList]);
  const selectedIsPrimary = useMemo(() => selectedIsPrimaryFrom(selectedScenarioList), [selectedScenarioList]);

  useEffect(() => {
    if (noneSelected) {
      props.setLastDrawnTotalValue(undefined);
      props.setLastDrawnCoreStockValue(undefined);
      props.setLastDrawnOtherStockValue(undefined);
      props.setLastDrawnCashValue(undefined);
    }
  }, [noneSelected]);

  const lgMediaQuery = useMediaQuery(theme.breakpoints.down("lg"));

  const scenarioCardStackDesktop = (
    <Stack direction="row" sx={{ py: 2, overflowX: "auto", flexShrink: 0, gap: 2 }}>
      {scenarioCardsStackAddCard}
      {scenarioCardsStackContents}
    </Stack>
  );

  const scenarioCardStackMobile = (
    <>
      {scenarioCardsStackAddCard}
      <Stack direction="row" sx={{ py: 2, overflowX: "auto", flexShrink: 0, gap: 1 }}>
        {scenarioCardsStackContents}
      </Stack>
    </>
  );

  const scenarioCardStack = isMobile ? scenarioCardStackMobile : scenarioCardStackDesktop;

  return (
    <Stack
      sx={{
        display: "flex",
        height: "100%",
        flexDirection: { xs: isMobile ? "column" : "column-reverse", lg: "row" },
        gap: isMobile ? 0 : 2,
      }}
    >
      {scenarioDialog}
      <Stack
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          minWidth: 0,
          justifyContent: "flex-start",
          gap: isMobile ? 0 : 2,
        }}
      >
        {isMobile ? null : (
          <ShadowPaper
            PaperProps={{
              className: "highlight-for-scenarios-tour-step-1 highlight-for-scenarios-tour-step-2",
              sx: {
                flexGrow: isSimple ? 0 : 1,
                sizing: "border-box",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                minHeight: 0,
                position: "relative",
              },
            }}
            locked={props.firstChecksDone && noPlansAdded && props.firstChecksDone && !noneSelected}
            padding={paperPadding}
            unlockButtonText="Add a Stock Plan or ESPP Plan to unlock the future Scenario Plot"
            handleUnlock={() => {
              props.immediateStockPlanAddPrompt();
              props.setPage("stock-plans");
            }}
          >
            {props.firstChecksDone ? (
              props.scenarioRetrievalErrored && !noPlansAdded ? (
                <ErrorPage checkYourInputsandRefresh />
              ) : (
                mainDisplayContents
              )
            ) : (
              <Skeleton variant="rectangular" sx={{ height: "100%" }} />
            )}
          </ShadowPaper>
        )}
        {noScenarios ? null : scenarioCardStack}
      </Stack>
      <Stack
        sx={{
          display: "flex",
          flexDirection: { xs: "row", lg: "column" },
          justifyContent: "flex-start",
          gap: 0,
          flexShrink: { xs: 1, lg: 0 },
          overflow: "auto",
        }}
      >
        {!props.firstChecksDone ? (
          <Paper
            sx={{
              width: fixedSizeForSideSquares,
              flexShrink: 0,
              padding: paperPadding,
              marginBottom: { xs: 0, lg: 2 },
              marginRight: { xs: 2, lg: 0 },
              boxSizing: "border-box",
            }}
          >
            <Skeleton variant="rectangular" width="100%" height={fixedSizeForSideSquares} />
          </Paper>
        ) : (
          <>
            {[
              {
                inCondition: props.firstChecksDone && noPlansAdded,
                boxProps: {
                  title: "Have stock plans?",
                  description:
                    "Enter some basic information about your stock plans to use the powerful modeling tools in this app.",
                  buttonText: "Configure your Stock Plans",
                  urgent: true,
                  onClick: () => {
                    props.immediateStockPlanAddPrompt();
                    props.setPage("stock-plans");
                  },
                },
              },
              {
                inCondition: noPlansAdded,
                boxProps: {
                  title: "Have ESPP plans?",
                  description:
                    "Enter some basic information about your ESPPs to use the powerful modeling tools in this app.",
                  buttonText: "Configure your ESPPs",
                  urgent: true,
                  onClick: () => {
                    props.immediateESPPAddPrompt();
                    props.setPage("espps");
                  },
                },
              },
              {
                inCondition:
                  !noPlansAdded &&
                  (selectedScenarioList === undefined || selectedScenarioList.length < 1) &&
                  props.firstChecksDone,
                boxProps: {
                  title: "Not sure where to start?",
                  description:
                    "Exploring views about your stock can be hard. It can help to see how various forecasts could impact your holdings over time. Click here to add some suggestions to get you started.",
                  buttonText: noPlansAdded ? "" : "Add example forecasts",
                  loading: props.loading,
                  onClick: () => handleAddOrSaveGenerator("SUGGESTED", true, false, false)([]),
                },
              },
              {
                inCondition:
                  !noPlansAdded &&
                  selectedScenarioList !== undefined &&
                  selectedScenarioList.length >= 1 &&
                  props.firstChecksDone &&
                  Object.keys(props.strategyMetadata).length === 0,
                boxProps: {
                  title: "Model a strategy in the future",
                  description:
                    "Now that you've set up a forecast, you can model a strategy on the Strategies page to see how it will perform in the future.",
                  buttonText: noPlansAdded ? "" : "Model a Strategy",
                  loading: props.loading,
                  onClick: () => props.setPage("strategies"),
                },
              },
              {
                inCondition: !noScenarios && props.firstChecksDone,
                boxProps: {
                  title: noneSelected
                    ? "Interested in learning more?"
                    : selectedIsPrimary
                    ? "This forecast is locked in!"
                    : "Exploring this forecast?",
                  description: nonePrimaryFrom(selectedScenarioList)
                    ? "Now that you've added a forecast option to explore in the future. Lock in your favorite, and we'll use it throughout projections like the ones on the Overview page."
                    : noneSelected
                    ? "Select a forecast option below or add some more to explore further!"
                    : selectedIsPrimary
                    ? "This forecast is locked in! You'll see it change some future charts and projections throughout the app like on the Overview page."
                    : "Want to explore this forecast further? Lock in to switch your selected forecast projections to this option. This forecast will be used throughout projections like those on the Overview page.",
                  disabled: noneSelected,
                  buttonText: noneSelected
                    ? "Select a forecast first"
                    : selectedIsPrimary
                    ? "View in overview"
                    : "Lock In Current Forecast",
                  onClick: () => {
                    if (selectedIsPrimary) {
                      props.setPage("overview");
                    } else {
                      const newSelectedScenarioList = selectedScenarioList.map((scenario) => ({
                        ...scenario,
                        primary: scenario.currentlyDisplayed, // makes the current one primary
                      }));
                      setSelectedScenarioList(newSelectedScenarioList);
                      const currentScenario = getCurrentlyDisplayedScenarioDisplay(selectedScenarioList);
                      if (currentScenario !== undefined) {
                        snackbarScenarioChange(props.addSnackbarMessage, currentScenario);
                      }
                      PostSelectedScenarioList(newSelectedScenarioList, props.handleOverviewUpdateNotification);
                    }
                  },
                },
              },
              {
                inCondition: props.firstWithholdingCheckDone,
                boxProps: {
                  title: props.withholdingOn ? "Tax withholding" : "Need to set up tax withholding?",
                  description: props.withholdingOn
                    ? "You have tax withholding set up and enabled. All values are net of " +
                      props.withholdingRate.toLocaleString("en-US", { style: "percent", maximumFractionDigits: 0 }) +
                      " tax withholding. These shares never end up in your hands, so our models remove them from all values."
                    : "It looks like you either haven't set up tax withholding or have it disabled. Models and values could appear high without accurate tax withholding in your profile.",
                  disabled: false,
                  buttonText: props.withholdingOn ? "Edit tax withholding" : "Set up tax withholding",
                  onClick: () => props.setPage("profile"),
                },
              },
              {
                inCondition: !noScenarios && props.firstChecksDone,
                boxProps: {
                  title: "Selling some shares?",
                  description:
                    "Use smart lot selection to minimize your tax burden. We'll identify which shares to sell that minimize your tax burden." +
                    (taxSavings <= 0
                      ? ""
                      : " Smart lot selection could have saved you $" +
                        Number(Number(taxSavings).toFixed(0)).toLocaleString("en-US") +
                        " on your selected forecast."),
                  disabled: true,
                  buttonText: "(Coming Soon)",
                  onClick: () => console.log("TODO"),
                },
              },
            ].map(
              (
                box // TODO: we can choose to show only the first 3 of these or something...factor this out into a function?
              ) => (
                <Collapse
                  key={box.boxProps.title}
                  in={box.inCondition}
                  orientation={lgMediaQuery ? "horizontal" : "vertical"}
                  sx={{
                    flexShrink: 0,
                  }}
                >
                  <Paper
                    sx={{
                      width: fixedSizeForSideSquares,
                      flexShrink: 0,
                      padding: paperPadding,
                      marginBottom: { xs: 0, lg: 2 },
                      marginRight: { xs: 2, lg: 0 },
                      boxSizing: "border-box",
                    }}
                  >
                    <CallToActionBox {...box.boxProps} />
                  </Paper>
                </Collapse>
              )
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
}

export default ScenariosPanel;
